import { useMutation, useQuery } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { ConnectionUser } from '../lib/entities/connection-user';
import { ReferenceEntity } from '../lib/entities/reference';
import { DateTimeFrame } from '../types/date-time-frame';
import { FeedbackApiData, FeedbackState } from '../types/feedback';
import { QueryKey } from '../types/query-key';
import { RecognitionApiData } from '../types/recognition';
import { UserResponseApiData } from '../types/user';
import { linkReferences } from '../utils/references';
import { gatherUserIds } from '../utils/users';

type FeedbackParams = {
	date?: DateTimeFrame;
	giverId?: string;
	state?: FeedbackState;
	userId?: string;
};
type FeedbackRequest = {
	giverId: string;
	receiverId: string;
	requestText: string;
	isSharedWithManager: boolean;
	isDraft?: boolean;
	reference?: ReferenceEntity;
};

type FeedbackRequestBatch = {
	giverIds: string[];
	receiverId: string;
	requestText: string;
	isSharedWithManager: boolean;
	isDraft?: boolean;
	reference?: ReferenceEntity;
};

type FeedbackTemplateRequestData = {
	giverId: string;
	receiverId: string;
	template: {
		[key: string]: string;
	};
};
type UpdateFeedbackTemplateRequestData = {
	feedbackId: string;
	template: {
		[key: string]: string;
	};
};
type UnsolicitedFeedbackRequestData = {
	giverId: string;
	receiverId: string;
	text: string;
	isDraft?: boolean;
	reference?: ReferenceEntity;
};

export const useCreateFeedbackRequest = (options = {}) => {
	const http = useHttpClient('v2');

	return useMutation(
		(data: FeedbackRequest) => {
			const state = data.isDraft ? 'draft' : 'published';
			const requested_at = state == 'published' ? Date.now() : '';

			const params = {
				giver_id: data.giverId,
				receiver_id: data.receiverId,
				shared_with_manager: data.isSharedWithManager,
				reference_type: data.reference?.type,
				reference_id: data.reference?.id,
				state: state,
				request: { text: data.requestText, requested_at: requested_at },
			};

			return http.post('/constructive_feedback/feedback', params);
		},
		{
			retry: false,
			...options,
		}
	);
};

export const useCreateFeedbackRequestBatch = (options = {}) => {
	const http = useHttpClient('v2');

	return useMutation(
		(data: FeedbackRequestBatch) => {
			const requested_at = data.isDraft ? '' : Date.now();

			const feedback = data.giverIds.map((giverId) => ({
				giver_id: giverId,
				receiver_id: data.receiverId,
				shared_with_manager: data.isSharedWithManager,
				reference_type: data.reference?.type,
				reference_id: data.reference?.id,
				state: data.isDraft ? 'draft' : 'published',
				request: { text: data.requestText, requested_at },
			}));

			return http.post('/constructive_feedback/feedback_requests_batch', { feedback });
		},
		{
			retry: false,
			...options,
		}
	);
};

export const useCreateUnsolicitedFeedback = (options = {}) => {
	const http = useHttpClient('v2');

	return useMutation(
		(data: UnsolicitedFeedbackRequestData) => {
			const state = data.isDraft ? 'draft' : 'published';

			const params = {
				giver_id: data.giverId,
				receiver_id: data.receiverId,
				text: data.text,
				shared_with_manager: true,
				reference_type: data.reference?.type,
				reference_id: data.reference?.id,
				state: state,
			};
			return http.post('/constructive_feedback/feedback', params);
		},
		{
			retry: false,
			...options,
		}
	);
};

export const useCreateUnsolicitedFeedbackFromTemplate = (options = {}) => {
	const http = useHttpClient('v2');

	return useMutation(
		(data: FeedbackTemplateRequestData) => {
			const params = {
				receiver_id: data.receiverId,
				template: data.template,
			};
			return http.post('/constructive_feedback/feedback_templates', params);
		},
		{
			retry: false,
			...options,
		}
	);
};

export const useUpdateUnsolicitedFeedbackFromTemplate = (options = {}) => {
	const http = useHttpClient('v2');

	return useMutation(
		(data: UpdateFeedbackTemplateRequestData) => {
			const params = {
				template: data.template,
			};
			return http.put(`/constructive_feedback/feedback_templates/${data.feedbackId}`, params);
		},
		{
			retry: false,
			...options,
		}
	);
};

export const useQueryListFeedback = (params: FeedbackParams) => {
	const company = useCurrentCompany();
	const http = useHttpClient('v2');

	return useQuery(
		[QueryKey.ConsolidatedFeedbackIndex, params],
		async () => {
			const evaluations = await http.get<FeedbackApiData[]>(`/constructive_feedback/feedback`, {
				params: {
					...(params.date && { date: params.date }),
					...(params.giverId && { giver_id: params.giverId }),
					...(params.state && { state: params.state }),
					...(params.userId && { user_id: params.userId }),
				},
			});

			const userIds = gatherUserIds(evaluations.data);
			const users = await http.get<UserResponseApiData>('/stronger_connections', {
				params: {
					company_id: company.id,
					user_ids: userIds.join(','),
				},
			});

			const referenceIds = evaluations.data.map((feedback) => feedback.reference_id).filter(Boolean);

			if (!referenceIds.length) {
				return {
					evaluations,
					references: { data: { result: [] } },
				};
			}
			const references = await http.get<{ result: RecognitionApiData[] }>('/bonuses', {
				params: { bonus_ids: referenceIds },
			});

			return { evaluations, references, users };
		},
		{
			select: (data) => {
				const evaluations = linkReferences(data.evaluations.data, data.references.data.result);

				return {
					evaluations,
					users: data.users?.data.result.map((user) => {
						return ConnectionUser.fromApiData(user);
					}),
				};
			},
		}
	);
};
